

import { useShow } from '@/contexts/ShowContext'
import { apiFetch } from '@/lib/api'
import  Button  from '@/components/ui/Button'
import { FiEdit, FiPlus, FiSave, FiTrash, FiX } from 'react-icons/fi'
import Loader from '@/components/loader'
import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/shadcn/input'
import { Textarea } from '@/components/ui/shadcn/textarea'
import FilePicker from '@/components/ui/FilePicker'
import useFileUpload from '@/hooks/useFileUpload'
import { useModal } from '@/contexts/ModalContext'
import ConfirmModal from '@/components/modals/ConfirmModal'
import ApiKeyModal from '@/components/modals/ApiKeyModal'

export default function SettingsPage() {
    const [account, setAccount] = useState({})
    const [keys, setKeys] = useState([])
    const [updating, setUpdating] = useState(true)
    const {openModal} = useModal()

    const fetchAccount = async()=>{
        try{
            const response = await apiFetch(`/api/account`)
            if(!response.ok){
                throw new Error('Failed to fetch account')
            }
            const data = await response.json()
            setAccount(data)
        }catch(error){
            console.error(error)
        }
    }

    const fetchKeys = async() =>{
        try{
            const response = await apiFetch(`/api/account/keys`)
            if(!response.ok){
                throw new Error('Failed to fetch keys')
            }
            const keys = await response.json()
            setKeys(keys)
        }catch(error){
            console.error(error)
        }
    }
    
    const handleAddKey = async() =>{
        const newApiKey = await openModal(<ApiKeyModal/>, 'side')
        if(!newApiKey) return
        
        try{
            setUpdating(true)
            const keyResponse = await apiFetch(`/api/account/keys`, {
                method: 'POST',
                body: JSON.stringify({name: newApiKey.name})
            })

            if(!keyResponse.ok) throw new Error('Failed to add API key')

            const data = await keyResponse.json()

            await openModal(<ConfirmModal title='New API Key' message={`New API Key "${data.name}" has been added. Copy the key below and save it in a secure location:\n${data.key}`}/>)

            await fetchKeys()
        }catch(error){
            console.error(error)
        }finally{
            setUpdating(false)
        }
    }

    const handleDeleteKey = async(keyId) =>{
        const confirmed = await openModal(<ConfirmModal title='Delete API Key' message='Are you sure you want to delete this API key?'/>)
        if(!confirmed) return

        try{
            setUpdating(true)
            const keysResponse =  await apiFetch(`/api/account/keys/${keyId}`, {
                method: 'DELETE'
            })

            if(!keysResponse.ok) throw new Error('Failed to delete API key')

            await fetchKeys()

        }catch(error){
            console.error(error)
        }finally{
            setUpdating(false)
            
        }
    }

    useEffect(()=>{
        const getData = async () =>{
            setUpdating(true)
            await fetchAccount()
            await fetchKeys()
            setUpdating(false)
        }
        getData()
    },[])

    return (
        <div className="flex h-full flex-col relative">
            <div className='flex justify-between items-center px-10 h-20'>
                <div className='flex gap-2'>
                    <h1 className='text-2xl font-bold border-l-4 border-primary pl-4'>Account Settings</h1>
                </div>
            </div>
            <div className='flex flex-col flex-1 w-full overflow-hidden'>
                <div className='flex flex-col px-10 py-4 flex-1 overflow-y-auto'>
                    <div className='flex flex-col gap-16 max-w-5xl'> 
                        <div className='flex flex-col'>
                            <div className='flex items-center justify-between border-b py-2'>
                                <h2 className='text-lg font-bold text-secondary-foreground'>Account Details</h2>
                            </div>
                            <div className='flex flex-col gap-2 py-2'>
                                <div className='flex items-center gap-2'>
                                    <span className='text-secondary-foreground text-sm min-w-64'>Name</span>
                                    <span className='text-secondary-foreground/80 text-xs'>{account.name}</span>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-secondary-foreground text-sm min-w-64'>WSC Account ID</span>
                                    <span className='text-secondary-foreground/80 text-xs'>{account.account_id}</span>
                                </div>
                            </div>
                        </div>

                        <div className='flex flex-col'>
                            <div className='flex items-center justify-between border-b py-2'>
                                <h2 className='text-lg font-bold text-secondary-foreground'>API Keys</h2>
                                <Button type='secondary' icon={<FiPlus className='w-4 h-4' />} onClick={handleAddKey}>Add API Key</Button>
                            </div>
                            <div className='flex flex-col'>
                                {keys.map((apiKey) => (
                                    <div key={apiKey._id} className='flex gap-2 py-1 items-center group/category justify-between'>
                                        <div className='flex items-center gap-1'>
                                            <span className='text-secondary-foreground text-sm capitalize min-w-64'>{apiKey.name}</span>
                                            <span className='text-secondary-foreground/80 text-xs'>{apiKey.key}</span>
                                        </div>
                                        <div className='flex gap-1 opacity-0 group-hover/category:opacity-100'>
                                            <Button type='danger' icon={<FiTrash className='w-4 h-4' />} onClick={() => handleDeleteKey(apiKey._id)} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>                        
                    </div>
                </div>
            </div>
            {updating && <Loader/>}
        </div>
    )
}
